import { notification } from "antd";
import instance from "api/cmsapi";
import { useMutation, useQuery, useQueryClient } from "react-query";

/* =========================================================
   API Requests DPAT Thematic Area
   ========================================================= */

// CREATE
export const createThematicArea = async (payload) =>
  await instance.post("dpat-thematic-area", payload);

// CREATE BULK
export const createThematicAreas = async (payload) =>
  await instance.post("dpat-thematic-area/bulk", payload);

// UPDATE
export const updateThematicArea = async ({ id, payload }) =>
  await instance.put(`dpat-thematic-area/${id}`, payload);

// GET ALL
export const getAllThematicAreas = async () =>
  await instance.get("dpat-thematic-area");

// GET BY ID
export const getThematicAreaById = async (id) =>
  await instance.get(`dpat-thematic-area/${id}`);

// GET BY NAME
export const getThematicAreaByName = async (thematicAreaName) =>
  await instance.get(`dpat-thematic-area/name/${thematicAreaName}`);

// GET BY CODE
export const getThematicAreaByCode = async (thematicAreaCode) =>
  await instance.get(`dpat-thematic-area/code/${thematicAreaCode}`);

/* =========================================================
   React Query Hooks DPAT Thematic Area
   ========================================================= */

// GET ALL
export function useGetAllThematicAreas() {
  return useQuery(
    ["getAllThematicAreas"],
    getAllThematicAreas,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching thematic areas",
        }),
    }
  );
}

// GET BY ID
export function useGetThematicAreaById(id) {
  return useQuery(
    ["getThematicAreaById", id],
    () => getThematicAreaById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching thematic area",
        }),
    }
  );
}

// GET BY NAME
export function useGetThematicAreaByName(thematicAreaName) {
  return useQuery(
    ["getThematicAreaByName", thematicAreaName],
    () => getThematicAreaByName(thematicAreaName),
    {
      enabled: !!thematicAreaName,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching thematic area",
        }),
    }
  );
}

// GET BY CODE
export function useGetThematicAreaByCode(thematicAreaCode) {
  return useQuery(
    ["getThematicAreaByCode", thematicAreaCode],
    () => getThematicAreaByCode(thematicAreaCode),
    {
      enabled: !!thematicAreaCode,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching thematic area",
        }),
    }
  );
}

// CREATE
export function useCreateThematicArea() {
  const queryClient = useQueryClient();

  return useMutation(createThematicArea, {
    onSuccess: () => {
      notification.success({
        message: "Thematic Area created successfully",
      });

      queryClient.invalidateQueries(["getAllThematicAreas"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating thematic area",
      }),
  });
}

// CREATE BULK
export function useCreateThematicAreas() {
  const queryClient = useQueryClient();

  return useMutation(createThematicAreas, {
    onSuccess: () => {
      notification.success({
        message: "Thematic Areas created successfully",
      });

      queryClient.invalidateQueries(["getAllThematicAreas"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating thematic areas",
      }),
  });
}

// UPDATE
export function useUpdateThematicArea() {
  const queryClient = useQueryClient();

  return useMutation(updateThematicArea, {
    onSuccess: () => {
      notification.success({
        message: "Thematic Area updated successfully",
      });

      queryClient.invalidateQueries(["getAllThematicAreas"]);
      queryClient.invalidateQueries(["getThematicAreaById"]);
      queryClient.invalidateQueries(["getThematicAreaByName"]);
      queryClient.invalidateQueries(["getThematicAreaByCode"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating thematic area",
      }),
  });
}


/* =========================================================
   API Requests DPAT Indicator
   ========================================================= */

// CREATE
export const createIndicator = async (payload) =>
  await instance.post("dpat-indicator", payload);

// CREATE BULK
export const createIndicators = async (payload) =>
  await instance.post("dpat-indicator/bulk", payload);

// CREATE UPLOAD
export const createIndicatorUpload = async (payload) =>
  await instance.post("dpat-indicator/upload", payload);

// UPDATE
export const updateIndicator = async ({ id, payload }) =>
  await instance.put(`dpat-indicator/${id}`, payload);

// GET ALL
export const getAllIndicators = async () =>
  await instance.get("dpat-indicator");

// GET BY ID
export const getIndicatorById = async (id) =>
  await instance.get(`dpat-indicator/${id}`);

// GET BY NAME
export const getIndicatorByName = async (indicatorName) =>
  await instance.get(`dpat-indicator/name/${indicatorName}`);

// GET BY YEAR
export const getIndicatorsByYear = async (year) =>
  await instance.get(`dpat-indicator/year/${year}`);

// GET BY THEMATIC AREA
export const getIndicatorsByThematicArea = async (thematicAreaId) =>
  await instance.get(`dpat-indicator/thematic-area/${thematicAreaId}`);

/* =========================================================
   React Query Hooks DPAT Indicator
   ========================================================= */

// GET ALL
export function useGetAllIndicators() {
  return useQuery(
    ["getAllIndicators"],
    getAllIndicators,
    {
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicators",
        }),
    }
  );
}

// GET BY ID
export function useGetIndicatorById(id) {
  return useQuery(
    ["getIndicatorById", id],
    () => getIndicatorById(id),
    {
      enabled: !!id,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicator",
        }),
    }
  );
}

// GET BY NAME
export function useGetIndicatorByName(indicatorName) {
  return useQuery(
    ["getIndicatorByName", indicatorName],
    () => getIndicatorByName(indicatorName),
    {
      enabled: !!indicatorName,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicator",
        }),
    }
  );
}

// GET BY YEAR
export function useGetIndicatorsByYear(year) {
  return useQuery(
    ["getIndicatorsByYear", year],
    () => getIndicatorsByYear(year),
    {
      enabled: !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicators by year",
        }),
    }
  );
}

// GET BY THEMATIC AREA
export function useGetIndicatorsByThematicArea(thematicAreaId) {
  return useQuery(
    ["getIndicatorsByThematicArea", thematicAreaId],
    () => getIndicatorsByThematicArea(thematicAreaId),
    {
      enabled: !!thematicAreaId,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicators by thematic area",
        }),
    }
  );
}

// CREATE
export function useCreateIndicator() {
  const queryClient = useQueryClient();

  return useMutation(createIndicator, {
    onSuccess: () => {
      notification.success({
        message: "Indicator created successfully",
      });

      queryClient.invalidateQueries(["getAllIndicators"]);
      queryClient.invalidateQueries(["getIndicatorsByThematicAndYear"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating indicator",
      }),
  });
}

// CREATE BULK
export function useCreateIndicators() {
  const queryClient = useQueryClient();

  return useMutation(createIndicators, {
    onSuccess: () => {
      notification.success({
        message: "Indicators created successfully",
      });

      queryClient.invalidateQueries(["getAllIndicators"]);
      queryClient.invalidateQueries(["getIndicatorsByYear"]);
      queryClient.invalidateQueries(["getIndicatorsByThematicAndYear"]);
      queryClient.invalidateQueries(["getIndicatorsByThematicArea"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating indicators",
      }),
  });
}

// CREATE UPLOAD
export function useCreateIndicatorUpload() {
  const queryClient = useQueryClient();

  return useMutation(createIndicatorUpload, {
    onSuccess: () => {
      notification.success({
        message: "Indicators uploaded successfully",
      });

      queryClient.invalidateQueries(["getAllIndicators"]);
      queryClient.invalidateQueries(["getIndicatorsByThematicAndYear"]);
      queryClient.invalidateQueries(["getIndicatorsByYear"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error creating indicators",
      }),
  });
}

// UPDATE
export function useUpdateIndicator() {
  const queryClient = useQueryClient();

  return useMutation(updateIndicator, {
    onSuccess: () => {
      notification.success({
        message: "Indicator updated successfully",
      });

      queryClient.invalidateQueries(["getIndicatorsByThematicAndYear"]);
      queryClient.invalidateQueries(["getIndicatorsByThematicArea"]);
    },
    onError: (error) =>
      notification.error({
        message:
          error?.response?.data?.message ||
          "Error updating indicator",
      }),
  });
}

// GET BY THEMATIC AREA AND YEAR
export const getIndicatorsByThematicAndYear = async (
  thematicAreaId,
  year
) =>
  await instance.get(
    `dpat-indicator/thematic-area/${thematicAreaId}/year/${year}`
  );

/* =========================================================
   React Query Hook - GET BY THEMATIC AREA AND YEAR
   ========================================================= */

export function useGetIndicatorsByThematicAndYear(
  thematicAreaId,
  year
) {
  return useQuery(
    ["getIndicatorsByThematicAndYear", thematicAreaId, year],
    () =>
      getIndicatorsByThematicAndYear(
        thematicAreaId,
        year
      ),
    {
      enabled: !!thematicAreaId && !!year,
      onError: (error) =>
        notification.error({
          message:
            error?.response?.data?.message ||
            "Error fetching indicators by thematic area and year",
        }),
    }
  );
}